// # Task Chapter Component for Step 1
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { getFirestore, doc, getDoc, setDoc, collection, updateDoc } from 'firebase/firestore';
import { format, parseISO } from 'date-fns';
import { calculateDates } from './task_calc1';
import { FaCalendarAlt } from 'react-icons/fa'; // # Import calendar icon
import TaskStats from './task_stats1'; // # Import TaskStats component
import cacheManager from '../../../auth/cache_manager';  // # Import cache manager
import { getTargetUserId } from '../m1_access';  // # Import access control utility
import './task_chapter1.css';

const TaskChapter1 = ({ isAdmin: propIsAdmin, isTutor: propIsTutor, isMentorView: propIsMentorView }) => {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, user } = useAuthStore();
  
  // # Get context values from props or from location state if available
  const isAdmin = propIsAdmin || (location.state && location.state.isAdmin) || false;
  const isTutor = propIsTutor || (location.state && location.state.isTutor) || false;
  const isMentorView = propIsMentorView || (location.state && location.state.isMentorView) || false;
  
  // # Get the correct target user ID based on role context
  const targetUserId = getTargetUserId({
    isAdmin,
    isTutor,
    viewingMenteeId: sessionStorage.getItem('currentViewingMenteeId'),
    currentUserId: user?.uid
  });
  
  // # Create base path based on role context
  const basePath = isAdmin ? '/admin/mentorship' : 
                  isTutor ? '/tutor/mentorship' : 
                  '/mentorship';
  
  // # Define source options
  const sourceOptions = [
    { value: '', label: 'Select source...' },
    { value: 'BnB', label: 'BnB' },
    { value: 'Pathoma', label: 'Pathoma' },
    { value: 'Husseiny', label: 'Husseiny' },
    { value: 'Ta7', label: 'Ta7' },
    { value: 'Physeo', label: 'Physeo' },
    { value: 'Sketchy Micro', label: 'Sketchy Micro' },
    { value: 'Picorize', label: 'Picorize' },
    { value: 'Mehlman', label: 'Mehlman' },
    { value: 'Dirty Medicine', label: 'Dirty Medicine' },
    { value: 'BootCamp', label: 'Boot Camp' },
  ];
  
  // # State for table data and controls
  const [rows, setRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleColumns, setVisibleColumns] = useState({
    task_id: false,
    step: false,
    chapter: false,
    task_name: true,
    source: true,
    plan: true,
    day: true,
    duration: true,
    type: true,
    date: true,
    done: true
  });
  const [showColumnsModal, setShowColumnsModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = useState({
    plan: '',
    type: '',
    day: '',
    duration: '',
    done: '',
    source: ''
  });

  // # State for import functionality
  const [isImporting, setIsImporting] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const [importError, setImportError] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  // # Add state for selected plans
  const [selectedPlans, setSelectedPlans] = useState({
    'Plan A': true,
    'Plan B': true,
    'Plan C': true
  });

  // # Add isEditing state
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // # Add sorting state - updated to sort by plan and then by task_name by default
  const [sortConfig, setSortConfig] = useState({
    key: ['plan', 'task_name'],
    direction: ['asc', 'asc']
  });

  // # Add state for calculate modal
  const [showCalculateModal, setShowCalculateModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [calculationStartDate, setCalculationStartDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  // # State for clear dialog
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearOption, setClearOption] = useState('all');
  const [selectedClearSource, setSelectedClearSource] = useState('');
  const [selectedClearPlan, setSelectedClearPlan] = useState('');

  // # Add dark mode state
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        const db = getFirestore();
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Load tasks from Firestore on component mount
  useEffect(() => {
    const loadTasks = async () => {
      if (!targetUserId) return;
      
      try {
        const db = getFirestore();
        // # Create a reference to the mentee document
        const menteeRef = doc(db, 'db1_mentee', targetUserId);
        
        // # Get the mentee document
        const menteeSnapshot = await getDoc(menteeRef);
        
        if (menteeSnapshot.exists()) {
          const menteeData = menteeSnapshot.data();
          // # Get tasks for specific chapter from the chapters map
          const chapterData = menteeData.chapters?.[chapterId] || {};
          const tasksData = chapterData.tasks || {};
          
          const taskRows = Object.entries(tasksData).map(([taskId, task]) => ({
            ...task,
            task_id: taskId
          }));
          
          setRows(taskRows);
        } else {
          // # Initialize empty document if it doesn't exist
          await setDoc(menteeRef, {
            chapters: {
              [chapterId]: {
                tasks: {},
                metadata: {
                  chapterName: getChapterTitle(),
                  lastUpdated: new Date(),
                  totalTasks: 0
                }
              }
            }
          });
          setRows([]);
        }
      } catch (error) {
        console.error('Error loading tasks:', error);
      }
    };
    
    loadTasks();
  }, [targetUserId, chapterId]);

  // # Handle back navigation with role context
  const handleBackNavigation = () => {
    // # Check if we're in mentor view with a mentee username
    const menteeUsername = sessionStorage.getItem('currentViewingMenteeUsername');
    
    if (isMentorView && menteeUsername) {
      // # Navigate back to the mentee's tasks list within mentor/tutor view
      const path = isAdmin ?
        `/mentor/step1/${menteeUsername}/tasks` :
        `/tutor/mentorship/step1/${menteeUsername}/tasks`;
      navigate(path, { state: { isAdmin, isTutor, isMentorView } });
    } else {
      // # Standard navigation without username
      navigate(`/mentorship/step1/tasks`, { state: { isAdmin, isTutor, isMentorView } });
    }
  };

  // # Handle column sort
  const handleSort = (key) => {
    let newDirection;
    let newKey;
    let newDirections;

    if (sortConfig.key.includes(key)) {
      // # If already sorting by this key
      const index = sortConfig.key.indexOf(key);
      if (sortConfig.direction[index] === 'asc') {
        // # Switch to descending
        newDirection = [...sortConfig.direction];
        newDirection[index] = 'desc';
        newKey = [...sortConfig.key];
      } else {
        // # Remove this sort key
        newKey = sortConfig.key.filter(k => k !== key);
        newDirection = sortConfig.direction.filter((_, i) => i !== index);
      }
    } else {
      // # Add new sort key
      newKey = [...sortConfig.key, key];
      newDirection = [...sortConfig.direction, 'asc'];
    }

    setSortConfig({
      key: newKey,
      direction: newDirection
    });
  };

  // # Reset sort to default
  const resetSort = () => {
    setSortConfig({
      key: ['plan', 'task_name'],
      direction: ['asc', 'asc']
    });
  };

  // # Sort rows function
  const getSortedRows = (rows) => {
    if (!sortConfig.key.length) return rows;

    return [...rows].sort((a, b) => {
      for (let i = 0; i < sortConfig.key.length; i++) {
        const key = sortConfig.key[i];
        const direction = sortConfig.direction[i];
        
        let aValue = a[key];
        let bValue = b[key];

        // # Handle numeric values
        if (key === 'day' || key === 'duration') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }

        if (aValue < bValue) {
          return direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return direction === 'asc' ? 1 : -1;
        }
      }
      return 0;
    });
  };

  // # Generate a task ID
  const generateTaskId = () => {
    // # Format chapter name: lowercase, remove spaces and special characters
    const formattedChapter = chapterId.toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
    
    // # Generate unique suffix (9 characters)
    const uniqueSuffix = Math.random().toString(36).substr(2, 9);
    
    return `task_${formattedChapter}_${uniqueSuffix}`;
  };

  // # Create a new empty row
  const createEmptyRow = () => ({
    task_id: generateTaskId(),
    step: 'step1',
    chapter: chapterId,
    task_name: '',
    source: '',
    plan: '',
    day: '',
    duration: '',
    type: ''
  });

  // # Handle column visibility toggle
  const handleColumnToggle = (columnName) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnName]: !prev[columnName]
    }));
  };

  // # Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // # Apply filters to rows
  const applyFilters = (rows) => {
    return rows.filter(row => {
      return Object.entries(filters).every(([field, value]) => {
        if (!value) return true;
        if (field === 'day' || field === 'duration') {
          return row[field].toString() === value;
        }
        if (field === 'done') {
          return row[field].toString() === value;
        }
        if (field === 'source') {
          return row[field] === value;
        }
        return row[field].toLowerCase().includes(value.toLowerCase());
      });
    });
  };

  // # Filter rows based on search query and filters
  const filteredRows = applyFilters(rows.filter(row => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      row.task_id.toLowerCase().includes(searchLower) ||
      row.task_name.toLowerCase().includes(searchLower) ||
      row.source.toLowerCase().includes(searchLower) ||
      row.type.toLowerCase().includes(searchLower)
    );
  }));

  // # Update filtered rows to include sorting
  const filteredAndSortedRows = getSortedRows(applyFilters(rows.filter(row => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      row.task_id.toLowerCase().includes(searchLower) ||
      row.task_name.toLowerCase().includes(searchLower) ||
      row.source.toLowerCase().includes(searchLower) ||
      row.type.toLowerCase().includes(searchLower)
    );
  })));

  // # Handle input change for any field
  const handleInputChange = (taskId, field, value) => {
    if (!isEditing) return;
    
    if (field === 'date' && value) {
      // # Convert the date to ISO string with 12 PM time
      const dateObj = new Date(value + 'T12:00:00');
      value = dateObj.toISOString();
    }
    
    setRows(prevRows =>
      prevRows.map(row =>
        row.task_id === taskId
          ? { ...row, [field]: value }
          : row
      )
    );
  };

  // # Handle checkbox change for done status
  const handleDoneChange = (taskId, checked) => {
    if (!isEditing) return;
    
    setRows(prevRows =>
      prevRows.map(row =>
        row.task_id === taskId
          ? { ...row, done: checked }
          : row
      )
    );
  };

  // # Handle save
  const handleSave = async () => {
    if (!targetUserId) return;
    
    setIsSaving(true);
    try {
      const db = getFirestore();
      const menteeRef = doc(db, 'db1_mentee', targetUserId);
      
      // # Get current mentee data
      const menteeSnapshot = await getDoc(menteeRef);
      let menteeData = menteeSnapshot.exists() ? menteeSnapshot.data() : {};
      
      // # Initialize chapters map if it doesn't exist
      if (!menteeData.chapters) {
        menteeData.chapters = {};
      }
      
      // # Update tasks data
      const tasksObject = {};
      rows.forEach(row => {
        tasksObject[row.task_id] = row;
      });
      
      // # Update chapter data
      menteeData.chapters[chapterId] = {
        tasks: tasksObject,
        metadata: {
          chapterName: getChapterTitle(),
          lastUpdated: new Date(),
          totalTasks: rows.length
        }
      };
      
      // # Save to Firestore
      await setDoc(menteeRef, menteeData);
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // # Handle cancel edit
  const handleCancelEdit = async () => {
    // # Reload original data
    try {
      const db = getFirestore();
      const menteeRef = doc(db, 'db1_mentee', targetUserId);
      const menteeSnapshot = await getDoc(menteeRef);
      
      if (menteeSnapshot.exists()) {
        const menteeData = menteeSnapshot.data();
        const chapterData = menteeData.chapters?.[chapterId] || {};
        const tasksData = chapterData.tasks || {};
        
        const taskRows = Object.entries(tasksData).map(([taskId, task]) => ({
          ...task,
          task_id: taskId
        }));
        
        setRows(taskRows);
      }
    } catch (error) {
      console.error('Error reloading data:', error);
    }
    setIsEditing(false);
  };

  // # Handle select all plans
  const handleSelectAllPlans = (checked) => {
    setSelectedPlans({
      'Plan A': checked,
      'Plan B': checked,
      'Plan C': checked
    });
  };

  // # Check if all plans are selected
  const areAllPlansSelected = () => {
    return Object.values(selectedPlans).every(selected => selected);
  };

  // # Reset selected plans to default (all selected)
  const resetSelectedPlans = () => {
    // # Reset all plans to selected (true)
    setSelectedPlans({
      'Plan A': true,
      'Plan B': true,
      'Plan C': true
    });
  };

  // # Handle import from master database
  const handleImport = async () => {
    if (!targetUserId) return;
    
    // # Check if at least one plan is selected
    const hasSelectedPlans = Object.values(selectedPlans).some(selected => selected);
    if (!hasSelectedPlans) {
      setImportError('Please select at least one plan to import.');
      return;
    }
    
    setIsImporting(true);
    setImportMessage('');
    setImportError('');
    
    try {
      // # Initialize functions with the correct region
      const functions = getFunctions(undefined, 'europe-west1');
      
      // # Call the function with the correct name
      const importTasksFromMaster = httpsCallable(functions, 'importTasksFromMaster');
      
      // # Get array of selected plan names
      const plansToImport = Object.keys(selectedPlans).filter(plan => selectedPlans[plan]);
      
      const result = await importTasksFromMaster({
        uid: targetUserId,
        step: 'step1',
        chapterId,
        overwrite: overwriteExisting,
        plans: plansToImport
      });
      
      const data = result.data;
      
      if (data.success) {
        setImportMessage(data.message);
        
        // # Reload tasks
        const db = getFirestore();
        const menteeRef = doc(db, 'db1_mentee', targetUserId);
        const menteeSnapshot = await getDoc(menteeRef);
        
        if (menteeSnapshot.exists()) {
          const menteeData = menteeSnapshot.data();
          const taskRows = Object.entries(menteeData.chapters[chapterId].tasks).map(([taskId, task]) => ({
            ...task,
            task_id: taskId
          }));
          
          setRows(taskRows);
        }
      } else {
        setImportError(data.error);
      }
    } catch (error) {
      console.error('Error importing tasks:', error);
      setImportError(`Error importing tasks: ${error.message}`);
    } finally {
      setIsImporting(false);
      setShowImportModal(false);
    }
  };

  // # Handle clear tasks
  const handleClear = () => {
    setShowClearModal(true);
  };

  // # Clear tasks based on selected option
  const clearTasks = async () => {
    if (!targetUserId) return;
    
    try {
      const db = getFirestore();
      const menteeRef = doc(db, 'db1_mentee', targetUserId);
      
      // # Get current mentee data
      const menteeSnapshot = await getDoc(menteeRef);
      let menteeData = menteeSnapshot.exists() ? menteeSnapshot.data() : {};
      
      // # Initialize chapters map if it doesn't exist
      if (!menteeData.chapters) {
        menteeData.chapters = {};
      }

      // # Filter tasks based on clear option
      let updatedTasks = {};
      if (clearOption === 'all') {
        // # Clear all tasks
        updatedTasks = {};
      } else {
        // # Keep tasks that don't match the clear criteria
        Object.entries(menteeData.chapters[chapterId].tasks || {}).forEach(([taskId, task]) => {
          if (
            (clearOption === 'source' && task.source !== selectedClearSource) ||
            (clearOption === 'plan' && task.plan !== selectedClearPlan)
          ) {
            updatedTasks[taskId] = task;
          }
        });
      }
      
      // # Update chapter data
      menteeData.chapters[chapterId] = {
        tasks: updatedTasks,
        metadata: {
          chapterName: getChapterTitle(),
          lastUpdated: new Date(),
          totalTasks: Object.keys(updatedTasks).length
        }
      };
      
      // # Save back to Firestore
      await setDoc(menteeRef, menteeData);
      
      // # Update local state
      setRows(Object.entries(updatedTasks).map(([taskId, task]) => ({
        ...task,
        task_id: taskId
      })));
      
    } catch (error) {
      console.error('Error clearing tasks:', error);
    }
    setShowClearModal(false);
  };

  // # Get chapter title - EXACT match with master database
  const getChapterTitle = () => {
    const chapterMap = {
      'Biochemistry': 'Biochemistry & Genetics',
      'Immunology': 'Immunology',
      'Microbiology': 'Microbiology',
      'General Pathology': 'General Pathology',
      'Pharmacology': 'Pharmacology',
      'Public Health': 'Public Health Sciences',
      'Anatomy': 'Anatomy & Histology',
      'Cardiovascular': 'Cardiovascular System',
      'Endocrine': 'Endocrine System',
      'Gastrointestinal': 'Gastrointestinal System',
      'Hematology': 'Hematology & Oncology',
      'Musculoskeletal': 'Musculoskeletal & Connective Tissue',
      'Dermatology': 'Dermatology',
      'Neurology': 'Neurology',
      'Special Senses': 'Special Senses',
      'Psychiatry': 'Behavioral Sciences',
      'Renal': 'Renal System',
      'Reproductive': 'Reproductive System',
      'Respiratory': 'Respiratory System',
      'Rapid Review': 'Rapid Review Series'
    };
    
    return chapterMap[chapterId] || chapterId;
  };

  // # Handle calculate dates
  const handleCalculateDates = async () => {
    if (!selectedPlan || !targetUserId) return;
    
    try {
      setIsSaving(true);
      const updatedRows = calculateDates(rows, selectedPlan, calculationStartDate);
      setRows(updatedRows);

      // # Save to Firestore
      const db = getFirestore();
      const menteeRef = doc(db, 'db1_mentee', targetUserId);
      
      // # Get current mentee data
      const menteeSnapshot = await getDoc(menteeRef);
      let menteeData = menteeSnapshot.exists() ? menteeSnapshot.data() : {};
      
      // # Initialize chapters map if it doesn't exist
      if (!menteeData.chapters) {
        menteeData.chapters = {};
      }
      
      // # Convert rows array to tasks object
      const tasksObject = {};
      updatedRows.forEach(row => {
        tasksObject[row.task_id] = row;
      });
      
      // # Update chapter data
      menteeData.chapters[chapterId] = {
        tasks: tasksObject,
        metadata: {
          chapterName: getChapterTitle(),
          lastUpdated: new Date(),
          totalTasks: updatedRows.length
        }
      };
      
      // # Save to Firestore
      await setDoc(menteeRef, menteeData);
      
      setShowCalculateModal(false);
      setSelectedPlan('');
      
      // # Show success message
      setImportMessage('Dates calculated and saved successfully');
      setTimeout(() => setImportMessage(''), 3000);
      
    } catch (error) {
      console.error('Error saving calculated dates:', error);
      setImportError('Failed to save calculated dates. Please try again.');
      setTimeout(() => setImportError(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={`task_chp1_container ${isDarkMode ? 'task_chp1_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <main className="task_chp1_main">
        <div className="task_chp1_header">
          <h1>{getChapterTitle()} Tasks</h1>
        </div>

        {/* Task Statistics Dashboard */}
        <TaskStats rows={rows} isDarkMode={isDarkMode} />

        {/* Action Buttons */}
        <div className="task_chp1_action_buttons">
          <button 
            className="task_chp1_action_button import"
            onClick={() => {
              resetSelectedPlans();
              setShowImportModal(true);
            }}
          >
            <i className="fas fa-file-import"></i> Import
          </button>
          <button 
            className="task_chp1_action_button calculate"
            onClick={() => setShowCalculateModal(true)}
          >
            <i className="fas fa-calculator"></i> Calculate
          </button>
          <div className="task_chp1_buttons_container">
            {isEditing ? (
              <>
                <button 
                  className="task_chp1_control_button cancel"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  <i className="fas fa-times"></i>
                  Cancel
                </button>
                <button 
                  className="task_chp1_control_button save"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  <i className="fas fa-save"></i>
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            ) : (
              <button 
                className="task_chp1_control_button edit"
                onClick={() => setIsEditing(true)}
              >
                <i className="fas fa-edit"></i>
                Edit Table
              </button>
            )}
          </div>
          <button 
            className="task_chp1_action_button clear"
            onClick={handleClear}
          >
            <i className="fas fa-trash-alt"></i> Clear
          </button>
        </div>

        {/* Status Messages */}
        {importMessage && (
          <div className="task_chp1_success_message">
            <i className="fas fa-check-circle"></i> {importMessage}
          </div>
        )}
        
        {importError && (
          <div className="task_chp1_error_message">
            <i className="fas fa-exclamation-circle"></i> {importError}
          </div>
        )}

        {/* Controls Bar */}
        <div className="task_chp1_controls_container">
          <div className="task_chp1_search_container">
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="task_chp1_search_input"
            />
          </div>
          
          <div className="task_chp1_buttons_container">
            <button 
              className="task_chp1_control_button reset-sort"
              onClick={resetSort}
            >
              <i className="fas fa-sort"></i>
              Reset Sort
            </button>
            <button 
              className="task_chp1_control_button columns"
              onClick={() => setShowColumnsModal(true)}
            >
              <i className="fas fa-columns"></i>
              Columns
            </button>
            
            <button 
              className="task_chp1_control_button filter"
              onClick={() => setShowFilterModal(true)}
            >
              <i className="fas fa-filter"></i>
              Filter
            </button>
          </div>
        </div>

        {/* Table Container */}
        <div className="task_chp1_table_container">
          <table className="task_chp1_table">
            <thead>
              <tr>
                {visibleColumns.task_id && (
                  <th data-column="task_id" onClick={() => handleSort('task_id')} className={sortConfig.key.includes('task_id') ? `sorted-${sortConfig.direction[sortConfig.key.indexOf('task_id')]}` : ''}>
                    Task ID {sortConfig.key.includes('task_id') && <i className={`fas fa-sort-${sortConfig.direction[sortConfig.key.indexOf('task_id')] === 'asc' ? 'up' : 'down'}`} />}
                  </th>
                )}
                {visibleColumns.step && (
                  <th data-column="step" onClick={() => handleSort('step')} className={sortConfig.key.includes('step') ? `sorted-${sortConfig.direction[sortConfig.key.indexOf('step')]}` : ''}>
                    Step {sortConfig.key.includes('step') && <i className={`fas fa-sort-${sortConfig.direction[sortConfig.key.indexOf('step')] === 'asc' ? 'up' : 'down'}`} />}
                  </th>
                )}
                {visibleColumns.chapter && (
                  <th data-column="chapter" onClick={() => handleSort('chapter')} className={sortConfig.key.includes('chapter') ? `sorted-${sortConfig.direction[sortConfig.key.indexOf('chapter')]}` : ''}>
                    Chapter {sortConfig.key.includes('chapter') && <i className={`fas fa-sort-${sortConfig.direction[sortConfig.key.indexOf('chapter')] === 'asc' ? 'up' : 'down'}`} />}
                  </th>
                )}
                {visibleColumns.task_name && (
                  <th data-column="task_name" onClick={() => handleSort('task_name')} className={sortConfig.key.includes('task_name') ? `sorted-${sortConfig.direction[sortConfig.key.indexOf('task_name')]}` : ''}>
                    Task Name {sortConfig.key.includes('task_name') && <i className={`fas fa-sort-${sortConfig.direction[sortConfig.key.indexOf('task_name')] === 'asc' ? 'up' : 'down'}`} />}
                  </th>
                )}
                {visibleColumns.source && (
                  <th data-column="source" onClick={() => handleSort('source')} className={`source-column ${sortConfig.key.includes('source') ? `sorted-${sortConfig.direction[sortConfig.key.indexOf('source')]}` : ''}`}>
                    Source {sortConfig.key.includes('source') && <i className={`fas fa-sort-${sortConfig.direction[sortConfig.key.indexOf('source')] === 'asc' ? 'up' : 'down'}`} />}
                  </th>
                )}
                {visibleColumns.plan && (
                  <th data-column="plan" onClick={() => handleSort('plan')} className={`plan-column ${sortConfig.key.includes('plan') ? `sorted-${sortConfig.direction[sortConfig.key.indexOf('plan')]}` : ''}`}>
                    Plan {sortConfig.key.includes('plan') && <i className={`fas fa-sort-${sortConfig.direction[sortConfig.key.indexOf('plan')] === 'asc' ? 'up' : 'down'}`} />}
                  </th>
                )}
                {visibleColumns.day && (
                  <th data-column="day" onClick={() => handleSort('day')} className={sortConfig.key.includes('day') ? `sorted-${sortConfig.direction[sortConfig.key.indexOf('day')]}` : ''}>
                    Day {sortConfig.key.includes('day') && <i className={`fas fa-sort-${sortConfig.direction[sortConfig.key.indexOf('day')] === 'asc' ? 'up' : 'down'}`} />}
                  </th>
                )}
                {visibleColumns.duration && (
                  <th data-column="duration" onClick={() => handleSort('duration')} className={sortConfig.key.includes('duration') ? `sorted-${sortConfig.direction[sortConfig.key.indexOf('duration')]}` : ''}>
                    Duration (min) {sortConfig.key.includes('duration') && <i className={`fas fa-sort-${sortConfig.direction[sortConfig.key.indexOf('duration')] === 'asc' ? 'up' : 'down'}`} />}
                  </th>
                )}
                {visibleColumns.type && (
                  <th data-column="type" onClick={() => handleSort('type')} className={sortConfig.key.includes('type') ? `sorted-${sortConfig.direction[sortConfig.key.indexOf('type')]}` : ''}>
                    Type {sortConfig.key.includes('type') && <i className={`fas fa-sort-${sortConfig.direction[sortConfig.key.indexOf('type')] === 'asc' ? 'up' : 'down'}`} />}
                  </th>
                )}
                {visibleColumns.date && (
                  <th data-column="date" onClick={() => handleSort('date')} className={sortConfig.key.includes('date') ? `sorted-${sortConfig.direction[sortConfig.key.indexOf('date')]}` : ''}>
                    Date {sortConfig.key.includes('date') && <i className={`fas fa-sort-${sortConfig.direction[sortConfig.key.indexOf('date')] === 'asc' ? 'up' : 'down'}`} />}
                  </th>
                )}
                {visibleColumns.done && (
                  <th data-column="done" onClick={() => handleSort('done')} className={sortConfig.key.includes('done') ? `sorted-${sortConfig.direction[sortConfig.key.indexOf('done')]}` : ''}>
                    Done {sortConfig.key.includes('done') && <i className={`fas fa-sort-${sortConfig.direction[sortConfig.key.indexOf('done')] === 'asc' ? 'up' : 'down'}`} />}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedRows.map(row => (
                <tr key={row.task_id} className={isEditing ? 'editing' : ''}>
                    {visibleColumns.task_id && <td data-column="task_id">{row.task_id}</td>}
                    {visibleColumns.step && <td data-column="step">{row.step}</td>}
                    {visibleColumns.chapter && <td data-column="chapter">{row.chapter}</td>}
                  {visibleColumns.task_name && (
                    <td data-column="task_name">
                      <input
                        type="text"
                        value={row.task_name}
                        onChange={(e) => handleInputChange(row.task_id, 'task_name', e.target.value)}
                        disabled={!isEditing}
                        className="task_chp1_table_input"
                      />
                    </td>
                  )}
                  {visibleColumns.source && (
                    <td className="source-column" data-source={row.source}>
                        <select
                          value={row.source}
                          onChange={(e) => handleInputChange(row.task_id, 'source', e.target.value)}
                        disabled={!isEditing}
                          className="task_chp1_table_select"
                        >
                          {sourceOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    )}
                  {visibleColumns.plan && (
                    <td className="plan-column" data-plan={row.plan}>
                      <select
                        value={row.plan}
                        onChange={(e) => handleInputChange(row.task_id, 'plan', e.target.value)}
                        disabled={!isEditing}
                        className="task_chp1_table_select"
                      >
                        <option value="">Select...</option>
                        <option value="Plan A">Plan A</option>
                        <option value="Plan B">Plan B</option>
                        <option value="Plan C">Plan C</option>
                      </select>
                    </td>
                  )}
                  {visibleColumns.day && (
                    <td data-column="day">
                      <input
                        type="number"
                        value={row.day}
                        onChange={(e) => handleInputChange(row.task_id, 'day', e.target.value)}
                        disabled={!isEditing}
                        className="task_chp1_table_input"
                        style={{ width: '80px' }}
                        min="0"
                        step="1"
                        onWheel={(e) => e.target.blur()}
                      />
                    </td>
                  )}
                  {visibleColumns.duration && (
                    <td data-column="duration">
                      <input
                        type="number"
                        value={row.duration}
                        onChange={(e) => handleInputChange(row.task_id, 'duration', e.target.value)}
                        disabled={!isEditing}
                        className="task_chp1_table_input"
                        style={{ width: '80px' }}
                      />
                    </td>
                  )}
                  {visibleColumns.type && (
                    <td data-column="type">
                      <input
                        type="text"
                        value={row.type}
                        onChange={(e) => handleInputChange(row.task_id, 'type', e.target.value)}
                        disabled={!isEditing}
                        className="task_chp1_table_input"
                      />
                    </td>
                  )}
                    {visibleColumns.date && (
                      <td data-column="date">
                      {isEditing ? (
                        <input
                          type="date"
                          value={row.date ? row.date.split('T')[0] : ''}
                          onChange={(e) => handleInputChange(row.task_id, 'date', e.target.value)}
                          className="task_chp1_table_input"
                        />
                      ) : (
                        <span className="task_chp1_date_display">
                          {row.date ? format(parseISO(row.date), 'MMM d, yyyy') : ''}
                        </span>
                      )}
                      </td>
                    )}
                  {visibleColumns.done && (
                      <td data-column="done">
                        <input
                          type="checkbox"
                        checked={row.done}
                        onChange={(e) => handleDoneChange(row.task_id, e.target.checked)}
                        disabled={!isEditing}
                          className="task_chp1_checkbox"
                        />
                      </td>
                    )}
                  </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Columns Modal */}
        {showColumnsModal && (
          <div className="task_chp1_modal_overlay">
            <div className="task_chp1_modal">
              <div className="task_chp1_modal_header">
                <h3>Visible Columns</h3>
                <button 
                  className="task_chp1_modal_close"
                  onClick={() => setShowColumnsModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="task_chp1_modal_content">
                <div className="task_chp1_column_options">
                  {Object.keys(visibleColumns).map(column => (
                    <div key={column} className="task_chp1_column_option">
                      <label>
                        <input
                          type="checkbox"
                          checked={visibleColumns[column]}
                          onChange={() => handleColumnToggle(column)}
                        />
                        {column.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="task_chp1_modal_footer">
                <button 
                  className="task_chp1_modal_button"
                  onClick={() => setShowColumnsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filter Modal */}
        {showFilterModal && (
          <div className="task_chp1_modal_overlay">
            <div className="task_chp1_modal">
              <div className="task_chp1_modal_header">
                <h3>Filter Tasks</h3>
                <button 
                  className="task_chp1_modal_close"
                  onClick={() => setShowFilterModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="task_chp1_modal_content">
                <div className="task_chp1_filter_options">
                  <div className="task_chp1_filter_option">
                    <label>Source:</label>
                    <select
                      value={filters.source}
                      onChange={(e) => handleFilterChange('source', e.target.value)}
                    >
                      <option value="">All</option>
                      {sourceOptions.filter(option => option.value).map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="task_chp1_filter_option">
                    <label>Plan:</label>
                    <select
                      value={filters.plan}
                      onChange={(e) => handleFilterChange('plan', e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="A">Plan A</option>
                      <option value="B">Plan B</option>
                      <option value="C">Plan C</option>
                    </select>
                  </div>
                  <div className="task_chp1_filter_option">
                    <label>Type:</label>
                    <input
                      type="text"
                      value={filters.type}
                      onChange={(e) => handleFilterChange('type', e.target.value)}
                      placeholder="Filter by type..."
                    />
                  </div>
                  <div className="task_chp1_filter_option">
                    <label>Day:</label>
                    <input
                      type="number"
                      value={filters.day}
                      onChange={(e) => handleFilterChange('day', e.target.value)}
                      placeholder="Filter by day..."
                    />
                  </div>
                  <div className="task_chp1_filter_option">
                    <label>Duration:</label>
                    <input
                      type="number"
                      value={filters.duration}
                      onChange={(e) => handleFilterChange('duration', e.target.value)}
                      placeholder="Filter by duration..."
                    />
                  </div>
                  <div className="task_chp1_filter_option">
                    <label>Done:</label>
                    <select
                      value={filters.done}
                      onChange={(e) => handleFilterChange('done', e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="task_chp1_modal_footer">
                <button 
                  className="task_chp1_modal_button clear"
                  onClick={() => {
                    setFilters({
                      plan: '',
                      type: '',
                      day: '',
                      duration: '',
                      done: '',
                      source: ''
                    });
                  }}
                >
                  Clear Filters
                </button>
                <button 
                  className="task_chp1_modal_button"
                  onClick={() => setShowFilterModal(false)}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Calculate Modal */}
        {showCalculateModal && (
          <div className="task_chp1_modal_overlay">
            <div className="task_chp1_modal">
              <div className="task_chp1_modal_header">
                <h3>Calculate Task Dates</h3>
                <button 
                  className="task_chp1_modal_close"
                  onClick={() => setShowCalculateModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="task_chp1_modal_content">
                <div className="task_chp1_filter_options">
                  <div className="task_chp1_filter_option">
                    <label>Select Plan:</label>
                    <select
                      value={selectedPlan}
                      onChange={(e) => setSelectedPlan(e.target.value)}
                      className="task_chp1_table_select"
                    >
                      <option value="">Select a plan...</option>
                      <option value="Plan A">Plan A</option>
                      <option value="Plan B">Plan B</option>
                      <option value="Plan C">Plan C</option>
                    </select>
                  </div>
                  <div className="task_chp1_filter_option">
                    <label>Start Date (Day 1):</label>
                    <input
                      type="date"
                      value={calculationStartDate}
                      onChange={(e) => setCalculationStartDate(e.target.value)}
                      className="task_chp1_table_input"
                    />
                  </div>
                  <p className="task_chp1_import_note">
                    This will set dates for all tasks in the selected plan based on their day numbers.
                    Day 1 will be set to the start date, Day 2 will be the next day, and so on.
                  </p>
                </div>
              </div>
              <div className="task_chp1_modal_footer">
                <button 
                  className="task_chp1_modal_button cancel"
                  onClick={() => setShowCalculateModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="task_chp1_modal_button calculate"
                  onClick={handleCalculateDates}
                  disabled={!selectedPlan}
                >
                  Calculate Dates
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Import Modal */}
        {showImportModal && (
          <div className="task_chp1_modal_overlay">
            <div className="task_chp1_modal">
              <div className="task_chp1_modal_header">
                <h3>Import Tasks</h3>
                <button 
                  className="task_chp1_modal_close"
                  onClick={() => setShowImportModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="task_chp1_modal_content">
                <p>
                  Import tasks from the master database for {getChapterTitle()}.
                </p>
                
                {/* Plan selection checkboxes */}
                <div className="task_chp1_import_plans">
                  <h4>Select plans to import:</h4>
                  <div className="task_chp1_plan_options">
                    <div className="task_chp1_plan_option select_all">
                      <label>
                        <input
                          type="checkbox"
                          checked={areAllPlansSelected()}
                          onChange={(e) => handleSelectAllPlans(e.target.checked)}
                        />
                        <strong>Select All</strong>
                      </label>
                    </div>
                    {Object.keys(selectedPlans).map(plan => (
                      <div key={plan} className="task_chp1_plan_option">
                        <label>
                          <input
                            type="checkbox"
                            checked={selectedPlans[plan]}
                            onChange={(e) => setSelectedPlans(prev => ({
                              ...prev,
                              [plan]: e.target.checked
                            }))}
                          />
                          {plan}
                        </label>
                      </div>
                    ))}
                  </div>
                  <p className="task_chp1_import_note">
                    Only tasks from the selected plans will be imported.
                  </p>
                </div>
                
                <div className="task_chp1_import_option">
                  <label>
                    <input
                      type="checkbox"
                      checked={overwriteExisting}
                      onChange={(e) => setOverwriteExisting(e.target.checked)}
                    />
                    Overwrite existing tasks
                  </label>
                  <p className="task_chp1_import_note">
                    {overwriteExisting 
                      ? 'Warning: This will replace all existing tasks for this chapter.' 
                      : 'Import will fail if tasks already exist for this chapter.'}
                  </p>
                </div>
              </div>
              <div className="task_chp1_modal_footer">
                <button 
                  className="task_chp1_modal_button cancel"
                  onClick={() => setShowImportModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="task_chp1_modal_button import"
                  onClick={handleImport}
                  disabled={isImporting || !Object.values(selectedPlans).some(selected => selected)}
                >
                  {isImporting ? 'Importing...' : 'Import Tasks'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Clear Modal */}
        {showClearModal && (
          <div className="task_chp1_modal_overlay">
            <div className="task_chp1_modal">
              <div className="task_chp1_modal_header">
                <h3>Clear Tasks</h3>
                <button 
                  className="task_chp1_modal_close"
                  onClick={() => setShowClearModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="task_chp1_modal_content">
                <div className="task_chp1_filter_options">
                  <div className="task_chp1_filter_option">
                    <label>Clear Option:</label>
                    <select
                      value={clearOption}
                      onChange={(e) => {
                        setClearOption(e.target.value);
                        setSelectedClearSource('');
                        setSelectedClearPlan('');
                      }}
                      className="task_chp1_table_select"
                    >
                      <option value="all">Clear All Tasks</option>
                      <option value="source">Clear by Source</option>
                      <option value="plan">Clear by Plan</option>
                    </select>
                  </div>

                  {clearOption === 'source' && (
                    <div className="task_chp1_filter_option">
                      <label>Select Source:</label>
                      <select
                        value={selectedClearSource}
                        onChange={(e) => setSelectedClearSource(e.target.value)}
                        className="task_chp1_table_select"
                      >
                        <option value="">Select source...</option>
                        {sourceOptions.filter(option => option.value).map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {clearOption === 'plan' && (
                    <div className="task_chp1_filter_option">
                      <label>Select Plan:</label>
                      <select
                        value={selectedClearPlan}
                        onChange={(e) => setSelectedClearPlan(e.target.value)}
                        className="task_chp1_table_select"
                      >
                        <option value="">Select plan...</option>
                        <option value="Plan A">Plan A</option>
                        <option value="Plan B">Plan B</option>
                        <option value="Plan C">Plan C</option>
                      </select>
                    </div>
                  )}

                  <p className="task_chp1_import_note">
                    {clearOption === 'all' 
                      ? 'Warning: This will remove all tasks for this chapter.' 
                      : clearOption === 'source'
                      ? 'This will remove all tasks from the selected source.'
                      : 'This will remove all tasks from the selected plan.'}
                  </p>
                </div>
              </div>
              <div className="task_chp1_modal_footer">
                <button 
                  className="task_chp1_modal_button cancel"
                  onClick={() => setShowClearModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="task_chp1_modal_button clear"
                  onClick={clearTasks}
                  disabled={
                    (clearOption === 'source' && !selectedClearSource) ||
                    (clearOption === 'plan' && !selectedClearPlan)
                  }
                >
                  Clear Tasks
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
};

export default TaskChapter1;
